import { ProjectSwitcher } from '@components/projects/ProjectSwitcher';
import { useWalletBalance } from '@hooks/useWalletBalance';
import { useAuthStore } from '@stores/auth.store';
import { Avatar, SearchInput, Tooltip, WalletBalanceWidget } from '@ui/index';
import { Bell, ChevronDown, ChevronRight, LogOut, User } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';



// --- Breadcrumb generator ----------------------------------------------------

const ROUTE_LABELS: Record<string, string> = {
  '': 'Dashboard',
  inbox: 'Inbox',
  contacts: 'Contacts',
  campaigns: 'Campaigns',
  templates: 'Templates',
  automation: 'Automation',
  catalog: 'Catalog',
  projects: 'Projects',
  analytics: 'Analytics',
  billing: 'Billing',
  settings: 'Settings',
  profile: 'Profile',
  admin: 'Super admin',
  new: 'Create new',
  edit: 'Edit',
  businesses: 'Businesses',
  reconciliation: 'Reconciliation',
};

function getBreadcrumbs(pathname: string): Array<{ label: string; href: string }> {
  const segments = pathname.split('/').filter(Boolean);
  const crumbs: Array<{ label: string; href: string }> = [];
  let path = '';
  for (const seg of segments) {
    path += `/${seg}`;
    const isId = seg.includes('-') && seg.length > 10;
    const label = isId ? '...' : ROUTE_LABELS[seg] ?? seg;
    crumbs.push({ label, href: path });
  }
  return crumbs;
}

// --- Component ---------------------------------------------------------------

export function TopBar(): JSX.Element {
  const navigate = useNavigate();
  const location = useLocation();
  const user = useAuthStore((s) => s.user);
  const clearAuth = useAuthStore((s) => s.clearAuth);
  const { balance: walletBalance } = useWalletBalance();

  const crumbs = getBreadcrumbs(location.pathname);
  const fullName = user
    ? `${user.first_name} ${user.last_name ?? ''}`.trim()
    : 'User';

  const handleLogout = (): void => {
    clearAuth();
    navigate('/login', { replace: true });
  };

  return (
    <header className="sticky top-0 z-30 flex h-16 shrink-0 items-center gap-4 border-b border-apple-divider bg-white/80 px-8 backdrop-blur-md">
      {/* Breadcrumbs */}
      <nav className="hidden items-center gap-1 text-[13px] md:flex" aria-label="Breadcrumb">
        <Link
          to="/"
          className="font-medium text-apple-gray-500 transition hover:text-apple-label"
        >
          Home
        </Link>
        {crumbs.map((c) => (
          <span key={c.href} className="flex items-center gap-1">
            <ChevronRight size={12} strokeWidth={1.5} className="text-apple-gray-400" />
            <Link
              to={c.href}
              className="font-medium text-apple-gray-600 transition hover:text-apple-label"
            >
              {c.label}
            </Link>
          </span>
        ))}
      </nav>

      {/* Project switcher — between breadcrumbs and search */}
      <div className="ml-auto">
        <ProjectSwitcher />
      </div>

      {/* Search */}
      <div className="w-64">
        <SearchInput
          placeholder="Search…"
          showShortcut
          onSearch={() => {}}
          containerClassName="h-9"
        />
      </div>

      {/* Wallet */}
      <WalletBalanceWidget balance={walletBalance} />

      {/* Notifications */}
      <Tooltip content="Notifications">
        <button
          type="button"
          className="relative flex h-9 w-9 items-center justify-center rounded-xl bg-apple-gray-100 text-apple-gray-600 transition hover:bg-apple-gray-200 hover:text-apple-label"
          aria-label="Notifications"
        >
          <Bell size={16} strokeWidth={1.5} />
          <span className="absolute -top-0.5 -right-0.5 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-gradient-danger px-1 text-[9px] font-bold text-white">
            3
          </span>
        </button>
      </Tooltip>

      {/* User menu — single trigger, opens a small dropdown with
          Profile and Sign out. Hover-revealed inline icons were
          replaced by an explicit menu so the controls are
          discoverable. */}
      <UserMenu
        fullName={fullName}
        email={user?.email ?? ''}
        avatarUrl={user?.avatar_url ?? undefined}
        onLogout={handleLogout}
      />
    </header>
  );
}

// ---------------------------------------------------------------------------
// UserMenu — top-right account dropdown. Trigger is an Avatar + name
// pill; menu lists Profile (→ /profile) and Sign out.
// ---------------------------------------------------------------------------

function UserMenu({
  fullName,
  email,
  avatarUrl,
  onLogout,
}: {
  fullName: string;
  email: string;
  avatarUrl?: string;
  onLogout: () => void;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent): void => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    const onEsc = (e: globalThis.KeyboardEvent): void => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onEsc);
    };
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        className="flex items-center gap-2 rounded-full bg-apple-gray-100 px-1 py-1 transition hover:bg-apple-gray-200"
      >
        <Avatar name={fullName} src={avatarUrl} size="sm" />
        <span className="hidden text-[12px] font-semibold text-apple-label lg:inline">
          {fullName}
        </span>
        <ChevronDown
          size={14}
          strokeWidth={1.75}
          className="mr-1 text-apple-gray-500"
        />
      </button>
      {open ? (
        <div
          role="menu"
          className="absolute right-0 top-full z-40 mt-2 w-60 overflow-hidden rounded-xl border border-apple-divider bg-white shadow-card"
        >
          <div className="border-b border-apple-divider px-4 py-3">
            <div className="flex items-center gap-2.5">
              <Avatar name={fullName} src={avatarUrl} size="md" />
              <div className="min-w-0">
                <div className="truncate text-[13px] font-semibold text-apple-label">
                  {fullName}
                </div>
                <div className="truncate text-[11px] text-apple-gray-600">
                  {email}
                </div>
              </div>
            </div>
          </div>
          <Link
            to="/profile"
            role="menuitem"
            onClick={() => setOpen(false)}
            className="flex w-full items-center gap-3 px-4 py-2.5 text-left text-[13px] text-apple-label transition hover:bg-apple-gray-50"
          >
            <User size={14} strokeWidth={1.75} className="text-apple-gray-600" />
            Profile
          </Link>
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              setOpen(false);
              onLogout();
            }}
            className="flex w-full items-center gap-3 border-t border-apple-divider px-4 py-2.5 text-left text-[13px] text-rose-600 transition hover:bg-rose-50"
          >
            <LogOut size={14} strokeWidth={1.75} />
            Sign out
          </button>
        </div>
      ) : null}
    </div>
  );
}
